import { faAngleDown } from "@jewlr/pro-regular-svg-icons/faAngleDown"
import { faAngleRight } from "@jewlr/pro-regular-svg-icons/faAngleRight"
import PropTypes from "prop-types"
import { useState } from "react"

import Modal, { closeModal } from "components/modal"
import Button from "components/ui/button"
import Divider from "components/ui/divider"
import Expand from "components/ui/expand"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import Switch from "components/ui/switch"
import { isTestMode } from "helpers/application"
import { getConsentState } from "helpers/consent"

const ConsentAccordion = ({ title, description, value, handleChange }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border-t border-black">
      <h4 className="flex items-center justify-between overflow-hidden">
        <Button
          className="desktop:py-20 items-center gap-8 py-16"
          onClick={() => setExpanded(!expanded)}
        >
          <FontAwesomeIcon
            className="text-24"
            icon={expanded ? faAngleDown : faAngleRight}
          />
          <span className="body font-semibold">{title}</span>
        </Button>
        <Switch
          checked={value}
          disabled={!handleChange}
          name={title}
          onChange={() => handleChange && handleChange(!value)}
        >
          <p className="sr-only">Grant {title} cookies consent</p>
        </Switch>
      </h4>
      <Expand role="region" show={expanded}>
        <p className="body-sm desktop:pb-20 pb-16">{description}</p>
      </Expand>
    </div>
  )
}

ConsentAccordion.propTypes = {
  description: PropTypes.string,
  handleChange: PropTypes.func,
  title: PropTypes.string,
  value: PropTypes.bool,
}

const ConsentModal = () => {
  const [consentState, setConsentState] = useState(getConsentState())
  const [analyticsEnabled, setAnalyticsEnabled] = useState(
    consentState?.analytics_storage === "granted"
  )
  const [advertisingEnabled, setAdvertisingEnabled] = useState(
    consentState?.ad_storage === "granted"
  )

  const refreshConsentState = () => {
    const consentState = getConsentState()
    if (consentState) {
      setConsentState(consentState)
    }
  }

  const handleSavePreferences = () => {
    const consent = {
      ad_storage: advertisingEnabled ? "granted" : "denied",
      analytics_storage: analyticsEnabled ? "granted" : "denied",
    }

    if (!isTestMode) {
      dataLayer.push({
        ...consent,
        event: "consent-update",
      })
    }

    window.dispatchEvent(new Event("consent_update"))
    closeModal()
  }

  const handleAcceptNecessary = () => {
    const consent = { ad_storage: "denied", analytics_storage: "denied" }

    if (!isTestMode) {
      dataLayer.push({
        ...consent,
        event: "consent-update",
      })
    }

    setAnalyticsEnabled(false)
    setAdvertisingEnabled(false)

    window.dispatchEvent(new Event("consent_update"))
    closeModal()
  }

  return (
    <Modal
      afterShow={refreshConsentState}
      label="Privacy Preferences"
      modalType="slideup"
      show="#privacy-preferences"
      variant="squircle"
      width="722px"
    >
      <Divider />
      <p className="body-sm desktop:my-32 mb-20 mt-24">
        When you visit our site, we may store or access information on your
        browser or device. This can include data about your usage, preferences,
        or device, and is typically used to make the site function as expected,
        improve performance, provide analytics, and deliver relevant
        advertising. This information does not usually identify you directly,
        but it helps us offer you a more personalized experience. Because we
        respect your privacy, you can choose not to allow certain types of data
        collection. Click on the different category headings to learn more and
        change our default settings. Please note that restricting certain types
        of data may impact your experience of the site and the services we can
        provide.
      </p>
      <h3 className="h3 desktop:mb-32 mb-20">Manage Preferences</h3>
      <div className="relative flex flex-col">
        <div className="mt-0 flex-1 overflow-auto">
          <ConsentAccordion
            description={
              "These cookies are essential for the proper functioning of our website and cannot be disabled in our systems. They are typically set in response to actions you take, such as adjusting your privacy settings, logging in, or completing forms. While you can configure your browser to block or alert you about these cookies, certain parts of the website may not function correctly as a result. These cookies do not collect or store any personally identifiable information."
            }
            title="Strictly Necessary Cookies"
            value={true}
          />
          <ConsentAccordion
            description={
              "These cookies allow us to collect information about how visitors use our website, such as which pages are visited most often and how users navigate through the site. The data collected helps us understand and improve the website’s performance and user experience. All information these cookies collect is aggregated and therefore anonymous. If you choose to disable these cookies, we will not be able to monitor site performance or make improvements based on user behavior."
            }
            handleChange={setAnalyticsEnabled}
            title="Analytics"
            value={analyticsEnabled}
          />
          <ConsentAccordion
            description={
              "These cookies are used to deliver advertisements that are more relevant to you and your interests. They may be set through our site by advertising partners to build a profile of your interests and show you relevant content on other websites. They do not store personal information directly but are based on uniquely identifying your browser and internet device. If you choose to disable these cookies, you will still see ads, but they may be less tailored to your preferences"
            }
            handleChange={setAdvertisingEnabled}
            title="Advertising"
            value={advertisingEnabled}
          />
        </div>
        <div className="desktop:flex-row tablet:gap-16 desktop:w-auto bottom-0 left-0 mt-32 flex w-full flex-col gap-12 bg-white px-16 py-24">
          <Button
            className="desktop:w-auto h-48 w-full"
            onClick={handleSavePreferences}
            size="sm"
            variant="secondary"
          >
            Save my preference
          </Button>
          <Button
            className="desktop:w-auto h-48 w-full"
            onClick={handleAcceptNecessary}
            size="sm"
            variant="primary"
          >
            Accept only necessary
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ConsentModal
